import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

const PARAM = "setupSource";

type ConsumeEntrySource = (
  source: string,
  pathname: string,
) => boolean | Promise<boolean>;

/**
 * Reads `?setupSource=...` from the URL. If present, calls the provided
 * handler with the value + the current pathname, then strips the param only
 * when the handler reports it was consumed.
 *
 * The pathname is forwarded so the consumer can route-match the URL to a
 * registered product (e.g. `/finance/disputes/...` → disputes), keeping
 * the entry path product-agnostic.
 *
 * Use cases:
 *   - `?setupSource=shopify_reclaim_install` — direct Shopify Reclaim install lands here
 *   - `?setupSource=homepage_card` — clicked the cross-sell card on the homepage
 *   - `?setupSource=email_cta` — clicked an email CTA
 *   - `?setupSource=widget` — clicked the cross-route widget reminder
 *
 * Hook is responsible for retry-safe consumption + URL cleanup. The caller
 * (provider) decides what to do with the source string and returns whether
 * the param can be removed.
 */
export function useEntrySourceParam(onConsume: ConsumeEntrySource): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const attemptedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const value = searchParams.get(PARAM);
    if (!value) {
      attemptedKeyRef.current = null;
      return;
    }
    const attemptKey = `${location.pathname}?${searchParams.toString()}`;
    if (attemptedKeyRef.current === attemptKey) {
      return;
    }
    attemptedKeyRef.current = attemptKey;

    let cancelled = false;
    void Promise.resolve(onConsume(value, location.pathname))
      .then((consumed) => {
        if (cancelled) {
          return;
        }
        if (!consumed) {
          attemptedKeyRef.current = null;
          return;
        }

        const next = new URLSearchParams(searchParams);
        next.delete(PARAM);
        setSearchParams(next, { replace: true });
      })
      .catch(() => {
        if (!cancelled) {
          attemptedKeyRef.current = null;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, location.pathname, onConsume]);
}
